import { isNil } from 'lodash'
import { PreheaterParameters } from '../../generated/graphql'
import { EnergyType } from '../EnergyFlow'
import {
  createHourlyProvided,
  createSecondaryHourlyFromEnvironment,
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
} from '../HourlyEnergyFlow'
import {
  SingleTypeHourlyVariables,
  SingleTypeProvider,
} from './common/SingleTypeProvider'

type ValidatedParameters = {
  relativePower: number
}

type PreheaterHourlyVariables = SingleTypeHourlyVariables & {
  availableWasteHeat?: number
}

/**
 * Uses a portion (defined by the relative power) of the available waste heat for heating.
 * If the available waste heat is not defined, provides from an infinite source with the defined relative power.
 */
export class PreheaterProvider extends SingleTypeProvider<
  PreheaterParameters,
  PreheaterHourlyVariables
> {
  public readonly providedEnergyTypes = [EnergyType.Heating]

  private validatedParameters!: ValidatedParameters

  protected initialize(): void {
    const { relativePower } = this.parameters

    if (isNil(relativePower)) {
      throw new Error('Relative power must be defined')
    }

    this.validatedParameters = { relativePower }
  }

  protected getHourlyVariables(
    hour: number,
    demands: PrimaryHourlyEnergyFlow[],
  ): PreheaterHourlyVariables {
    return {
      ...super.getHourlyVariables(hour, demands),
      availableWasteHeat: this.hourSeries[0]?.values[hour],
    }
  }

  protected provideForHour({
    remainingDemand,
    availableWasteHeat,
  }: PreheaterHourlyVariables): HourlyEnergyFlow[] {
    const { relativePower } = this.validatedParameters

    const maxPower = remainingDemand.value * relativePower

    const providedHeatValue = isNil(availableWasteHeat)
      ? maxPower
      : Math.min(maxPower, availableWasteHeat)

    const providedHeating = createHourlyProvided(
      remainingDemand.demandId,
      remainingDemand.energyType,
      providedHeatValue,
    )

    const fromEnvironment = createSecondaryHourlyFromEnvironment(
      providedHeating.demandId,
      EnergyType.Heating,
      providedHeating.value,
    )

    return [providedHeating, fromEnvironment]
  }
}
